/**
 * Hellfire Scene — massive cavern interior looking out at a distant erupting
 * volcano. Wide irregular cave opening with rocky walls, stalactites,
 * rising embers, volcanic glow.
 */

import { Graphics } from 'pixi.js';
import type { StageScene, ParallaxLayers } from '../stage-scene';
import type { StageTheme } from '../../../stages';
import { WORLD } from '../../../constants';
import { FallingParticle } from '../particles/falling-particle';

interface Stalactite { gfx: Graphics; baseX: number; baseY: number; sway: number }
interface LavaFlow { gfx: Graphics; phase: number; baseAlpha: number }

export class HellfireScene implements StageScene {
  private stalactites: Stalactite[] = [];
  private embers: FallingParticle[] = [];
  private lavaFlows: LavaFlow[] = [];
  private volcanoGlow: Graphics | null = null;
  private eruptionGlow: Graphics | null = null;
  private layers: ParallaxLayers | null = null;

  build(layers: ParallaxLayers, _theme: StageTheme): void {
    this.layers = layers;
    const W = WORLD.VIRTUAL_WIDTH;
    const H = WORLD.VIRTUAL_HEIGHT;

    // ── Sky Layer: Distant volcanic sky (fills most of background) ──

    const caveBg = new Graphics();
    caveBg.rect(0, 0, W, H);
    caveBg.fill({ color: 0x080002, alpha: 1.0 });
    layers.sky.container.addChild(caveBg);

    // Warm volcanic sky visible through the wide cavern mouth
    const distantSky = new Graphics();
    distantSky.ellipse(W * 0.5, H * 0.3, W * 0.55, H * 0.45);
    distantSky.fill({ color: 0x180808, alpha: 0.7 });
    layers.sky.container.addChild(distantSky);

    // Smoky atmospheric haze
    const haze = new Graphics();
    haze.ellipse(W * 0.5, H * 0.35, W * 0.4, H * 0.25);
    haze.fill({ color: 0x200a05, alpha: 0.35 });
    layers.sky.container.addChild(haze);

    // ── Deep Layer: Volcano + eruption ──

    const volcX = W * 0.7;
    const volcBaseY = H * 0.55;
    const volcPeakY = H * 0.12;

    // Volcano silhouette — larger since view is wider
    const volcano = new Graphics();
    volcano.moveTo(volcX - 350, volcBaseY);
    volcano.lineTo(volcX - 40, volcPeakY);
    volcano.lineTo(volcX + 40, volcPeakY);
    volcano.lineTo(volcX + 350, volcBaseY);
    volcano.closePath();
    volcano.fill({ color: 0x0a0002, alpha: 0.85 });
    layers.deep.container.addChild(volcano);

    // Eruption glow at crater (pulsing orange)
    this.eruptionGlow = new Graphics();
    this.eruptionGlow.circle(volcX, volcPeakY - 8, 50);
    this.eruptionGlow.fill({ color: 0xff4400, alpha: 0.25 });
    this.eruptionGlow.circle(volcX, volcPeakY - 8, 25);
    this.eruptionGlow.fill({ color: 0xff6600, alpha: 0.45 });
    this.eruptionGlow.circle(volcX, volcPeakY - 8, 10);
    this.eruptionGlow.fill({ color: 0xffaa44, alpha: 0.65 });
    layers.deep.container.addChild(this.eruptionGlow);

    // Volcano base lava glow
    this.volcanoGlow = new Graphics();
    this.volcanoGlow.ellipse(volcX, volcBaseY, 400, 50);
    this.volcanoGlow.fill({ color: 0xff2200, alpha: 0.08 });
    this.volcanoGlow.ellipse(volcX, volcBaseY, 250, 25);
    this.volcanoGlow.fill({ color: 0xff4400, alpha: 0.06 });
    layers.deep.container.addChild(this.volcanoGlow);

    // Lava rivers down main volcano slopes
    const lavaLines: [number, number, number, number][] = [
      [volcX - 100, volcPeakY + 60, volcX - 280, volcBaseY - 15],
      [volcX + 80, volcPeakY + 50, volcX + 250, volcBaseY - 20],
      [volcX + 15, volcPeakY + 30, volcX + 80, volcBaseY - 8],
      [volcX - 50, volcPeakY + 80, volcX - 180, volcBaseY - 5],
    ];
    for (const [x1, y1, x2, y2] of lavaLines) {
      const lava = new Graphics();
      lava.moveTo(x1, y1);
      lava.lineTo(x2, y2);
      lava.stroke({ color: 0xff4400, alpha: 0.25, width: 2 });
      layers.deep.container.addChild(lava);
      this.lavaFlows.push({ gfx: lava, phase: Math.random() * Math.PI * 2, baseAlpha: 0.25 });
    }

    // ── Secondary volcanoes — smaller, left side, no eruption ──

    // Medium volcano (left-center)
    const v2X = W * 0.3;
    const v2BaseY = H * 0.52;
    const v2PeakY = H * 0.22;
    const v2 = new Graphics();
    v2.moveTo(v2X - 200, v2BaseY);
    v2.lineTo(v2X - 25, v2PeakY);
    v2.lineTo(v2X + 25, v2PeakY);
    v2.lineTo(v2X + 200, v2BaseY);
    v2.closePath();
    v2.fill({ color: 0x070001, alpha: 0.75 });
    layers.deep.container.addChild(v2);

    // Dim crater glow — smoldering, not erupting
    const v2Glow = new Graphics();
    v2Glow.circle(v2X, v2PeakY - 4, 20);
    v2Glow.fill({ color: 0xff3300, alpha: 0.12 });
    v2Glow.circle(v2X, v2PeakY - 4, 8);
    v2Glow.fill({ color: 0xff5500, alpha: 0.2 });
    layers.deep.container.addChild(v2Glow);
    this.lavaFlows.push({ gfx: v2Glow, phase: 2.1, baseAlpha: 0.15 });

    // Lava veins on medium volcano
    const v2Lava: [number, number, number, number][] = [
      [v2X - 40, v2PeakY + 40, v2X - 140, v2BaseY - 10],
      [v2X + 30, v2PeakY + 35, v2X + 120, v2BaseY - 12],
    ];
    for (const [x1, y1, x2, y2] of v2Lava) {
      const lava = new Graphics();
      lava.moveTo(x1, y1);
      lava.lineTo(x2, y2);
      lava.stroke({ color: 0xff3300, alpha: 0.15, width: 1.5 });
      layers.deep.container.addChild(lava);
      this.lavaFlows.push({ gfx: lava, phase: Math.random() * Math.PI * 2, baseAlpha: 0.15 });
    }

    // Small volcano (far left)
    const v3X = W * 0.12;
    const v3BaseY = H * 0.50;
    const v3PeakY = H * 0.30;
    const v3 = new Graphics();
    v3.moveTo(v3X - 120, v3BaseY);
    v3.lineTo(v3X - 15, v3PeakY);
    v3.lineTo(v3X + 15, v3PeakY);
    v3.lineTo(v3X + 120, v3BaseY);
    v3.closePath();
    v3.fill({ color: 0x060001, alpha: 0.65 });
    layers.deep.container.addChild(v3);

    // Faint crater glow — barely active
    const v3Glow = new Graphics();
    v3Glow.circle(v3X, v3PeakY - 3, 12);
    v3Glow.fill({ color: 0xff2200, alpha: 0.08 });
    v3Glow.circle(v3X, v3PeakY - 3, 5);
    v3Glow.fill({ color: 0xff4400, alpha: 0.15 });
    layers.deep.container.addChild(v3Glow);
    this.lavaFlows.push({ gfx: v3Glow, phase: 4.0, baseAlpha: 0.10 });

    // Single lava vein on small volcano
    const v3Lava = new Graphics();
    v3Lava.moveTo(v3X + 10, v3PeakY + 25);
    v3Lava.lineTo(v3X + 70, v3BaseY - 8);
    v3Lava.stroke({ color: 0xff2200, alpha: 0.10, width: 1 });
    layers.deep.container.addChild(v3Lava);
    this.lavaFlows.push({ gfx: v3Lava, phase: 1.5, baseAlpha: 0.10 });

    // Atmospheric haze bands
    for (let i = 0; i < 4; i++) {
      const band = new Graphics();
      const bandY = H * 0.25 + i * 50;
      band.ellipse(W * 0.5, bandY, W * 0.35, 18);
      band.fill({ color: 0x331100, alpha: 0.05 + i * 0.015 });
      layers.deep.container.addChild(band);
    }

    // ── Mid Layer: Massive cavern framing ──
    // The cavern opening is WIDE — most of the screen — with irregular
    // rocky walls closing in from left, right, top, and bottom.

    const caveWalls = new Graphics();

    // LEFT CAVE WALL — thick irregular rock, organic bulges
    caveWalls.moveTo(0, 0);
    caveWalls.lineTo(0, H);
    // Floor rises up from bottom-left
    caveWalls.lineTo(W * 0.08, H);
    caveWalls.quadraticCurveTo(W * 0.12, H * 0.82, W * 0.06, H * 0.75);
    // Big rocky outcropping on left side
    caveWalls.quadraticCurveTo(W * 0.14, H * 0.65, W * 0.16, H * 0.55);
    caveWalls.quadraticCurveTo(W * 0.18, H * 0.45, W * 0.13, H * 0.35);
    // Wall narrows going up
    caveWalls.quadraticCurveTo(W * 0.10, H * 0.25, W * 0.12, H * 0.18);
    // Arch across the top — wide, irregular, peaks high
    caveWalls.quadraticCurveTo(W * 0.20, H * 0.06, W * 0.35, H * 0.04);
    caveWalls.quadraticCurveTo(W * 0.50, H * -0.02, W * 0.65, H * 0.04);
    caveWalls.quadraticCurveTo(W * 0.80, H * 0.06, W * 0.88, H * 0.18);
    // RIGHT CAVE WALL — mirror-ish but not symmetric
    caveWalls.quadraticCurveTo(W * 0.90, H * 0.25, W * 0.87, H * 0.32);
    // Rocky outcropping on right side
    caveWalls.quadraticCurveTo(W * 0.84, H * 0.42, W * 0.86, H * 0.52);
    caveWalls.quadraticCurveTo(W * 0.88, H * 0.62, W * 0.92, H * 0.72);
    // Floor rises up from bottom-right
    caveWalls.quadraticCurveTo(W * 0.90, H * 0.82, W * 0.92, H);
    caveWalls.lineTo(W, H);
    caveWalls.lineTo(W, 0);
    caveWalls.closePath();
    caveWalls.fill({ color: 0x060001, alpha: 0.95 });
    layers.mid.container.addChild(caveWalls);

    // Edge glow along the cave opening — warm red/orange from volcano light
    const edgeGlow = new Graphics();
    // Trace the inner edge of the opening
    edgeGlow.moveTo(W * 0.06, H * 0.75);
    edgeGlow.quadraticCurveTo(W * 0.14, H * 0.65, W * 0.16, H * 0.55);
    edgeGlow.quadraticCurveTo(W * 0.18, H * 0.45, W * 0.13, H * 0.35);
    edgeGlow.quadraticCurveTo(W * 0.10, H * 0.25, W * 0.12, H * 0.18);
    edgeGlow.quadraticCurveTo(W * 0.20, H * 0.06, W * 0.35, H * 0.04);
    edgeGlow.quadraticCurveTo(W * 0.50, H * -0.02, W * 0.65, H * 0.04);
    edgeGlow.quadraticCurveTo(W * 0.80, H * 0.06, W * 0.88, H * 0.18);
    edgeGlow.quadraticCurveTo(W * 0.90, H * 0.25, W * 0.87, H * 0.32);
    edgeGlow.quadraticCurveTo(W * 0.84, H * 0.42, W * 0.86, H * 0.52);
    edgeGlow.quadraticCurveTo(W * 0.88, H * 0.62, W * 0.92, H * 0.72);
    edgeGlow.stroke({ color: 0x551100, alpha: 0.35, width: 4 });
    layers.mid.container.addChild(edgeGlow);

    // Secondary inner rock formations — smaller outcroppings adding depth
    // Left alcove/shelf
    const leftShelf = new Graphics();
    leftShelf.moveTo(W * 0.10, H * 0.4);
    leftShelf.quadraticCurveTo(W * 0.18, H * 0.38, W * 0.20, H * 0.42);
    leftShelf.quadraticCurveTo(W * 0.18, H * 0.46, W * 0.12, H * 0.48);
    leftShelf.closePath();
    leftShelf.fill({ color: 0x080202, alpha: 0.7 });
    layers.mid.container.addChild(leftShelf);

    // Right alcove/shelf
    const rightShelf = new Graphics();
    rightShelf.moveTo(W * 0.90, H * 0.38);
    rightShelf.quadraticCurveTo(W * 0.82, H * 0.36, W * 0.80, H * 0.40);
    rightShelf.quadraticCurveTo(W * 0.82, H * 0.44, W * 0.88, H * 0.46);
    rightShelf.closePath();
    rightShelf.fill({ color: 0x080202, alpha: 0.7 });
    layers.mid.container.addChild(rightShelf);

    // Stalactites hanging from the wide arch
    const stalPositions = [
      { t: 0.15, lenMul: 1.0 }, { t: 0.22, lenMul: 0.6 },
      { t: 0.30, lenMul: 1.2 }, { t: 0.38, lenMul: 0.5 },
      { t: 0.44, lenMul: 0.8 }, { t: 0.52, lenMul: 0.4 },
      { t: 0.58, lenMul: 0.9 }, { t: 0.65, lenMul: 0.6 },
      { t: 0.72, lenMul: 1.1 }, { t: 0.78, lenMul: 0.5 },
      { t: 0.85, lenMul: 0.7 },
    ];
    for (const sp of stalPositions) {
      const gfx = new Graphics();
      const sx = W * sp.t;
      // Arch height at this x position (rough approximation)
      const archProgress = Math.abs(sp.t - 0.5) * 2; // 0 at center, 1 at edges
      const archY = H * (0.02 + 0.16 * archProgress * archProgress);
      const sy = archY;
      const len = (50 + Math.random() * 70) * sp.lenMul;
      const w = 5 + Math.random() * 8;

      gfx.moveTo(sx - w / 2, sy);
      gfx.lineTo(sx, sy + len);
      gfx.lineTo(sx + w / 2, sy);
      gfx.closePath();
      gfx.fill({ color: 0x100303, alpha: 0.85 });
      // Glowing tip
      gfx.circle(sx, sy + len, 2);
      gfx.fill({ color: 0xff3300, alpha: 0.35 });
      layers.mid.container.addChild(gfx);
      this.stalactites.push({ gfx, baseX: sx, baseY: sy, sway: 0 });
    }

    // Cave floor stalagmites (from bottom, left and right clusters)
    const floorRocks = [
      { x: W * 0.04, h: 50 }, { x: W * 0.08, h: 80 }, { x: W * 0.11, h: 40 },
      { x: W * 0.89, h: 60 }, { x: W * 0.93, h: 75 }, { x: W * 0.96, h: 45 },
    ];
    for (const rock of floorRocks) {
      const gfx = new Graphics();
      const baseY = H * 0.82;
      const w = 8 + Math.random() * 8;
      gfx.moveTo(rock.x - w / 2, baseY);
      gfx.lineTo(rock.x, baseY - rock.h);
      gfx.lineTo(rock.x + w / 2, baseY);
      gfx.closePath();
      gfx.fill({ color: 0x0a0202, alpha: 0.75 });
      layers.mid.container.addChild(gfx);
    }

    // Rising embers (70 — spread wider since opening is wider)
    for (let i = 0; i < 70; i++) {
      const x = W * 0.1 + Math.random() * W * 0.8;
      const y = Math.random() * H;
      const size = 0.5 + Math.random() * 1.8;
      const baseAlpha = 0.2 + Math.random() * 0.5;

      this.embers.push(new FallingParticle(layers.mid.container, {
        x, y, size,
        color: 0xff6622,
        alpha: baseAlpha,
        speed: -(15 + Math.random() * 30), // Negative speed so it rises
        drift: 0, // Handled manually by wobble in update
        wobblePhase: Math.random() * Math.PI * 2,
      }));
    }
  }

  update(dt: number, elapsed: number): void {
    const H = WORLD.VIRTUAL_HEIGHT;
    const W = WORLD.VIRTUAL_WIDTH;

    // Eruption glow pulse
    if (this.eruptionGlow) {
      const pulse = Math.sin(elapsed * 1.5);
      const flicker = Math.sin(elapsed * 7) * 0.05;
      this.eruptionGlow.alpha = 0.7 + 0.3 * pulse + flicker;
    }

    // Volcano base glow pulse (slower)
    if (this.volcanoGlow) {
      this.volcanoGlow.alpha = 0.8 + 0.2 * Math.sin(elapsed * 0.8);
    }

    // Lava flow brightness oscillation
    for (const lava of this.lavaFlows) {
      lava.gfx.alpha = lava.baseAlpha + 0.15 * Math.sin(elapsed * 2.5 + lava.phase);
    }

    // Stalactites — sway decay
    for (const stal of this.stalactites) {
      stal.sway *= Math.pow(0.94, dt * 60);
      stal.gfx.x = stal.baseX + Math.sin(elapsed * 8) * stal.sway;
    }

    // Embers — rise upward with wobble + flicker
    for (const ember of this.embers) {
      // Use the generic falling update but override the lateral position for wobble
      ember.update(dt, elapsed, W, H);

      // Override its x position for the strong wobble
      ember.gfx.x = ember.x + Math.sin(elapsed * 3 + ember.wobblePhase) * 6;

      // Dynamic alpha flicker based on wobble phase
      ember.gfx.alpha = ember.baseAlpha * (0.4 + 0.6 * Math.sin(elapsed * 5 + ember.wobblePhase));
    }
  }

  onExplosion(worldX: number, _worldY: number, radius: number): void {
    for (const stal of this.stalactites) {
      const dx = worldX - stal.baseX;
      const dist = Math.abs(dx);
      const falloff = Math.max(0, 1 - dist / 800);
      stal.sway += Math.sign(-dx) * falloff * 15;
    }
    if (this.eruptionGlow) {
      this.eruptionGlow.alpha = Math.min(1, 0.8 + 0.3 * (radius / 80));
    }
  }

  destroy(): void {
    this.stalactites = [];
    this.embers = [];
    this.lavaFlows = [];
    this.volcanoGlow = null;
    this.eruptionGlow = null;
    if (this.layers) {
      this.layers.sky.clear();
      this.layers.deep.clear();
      this.layers.mid.clear();
    }
  }
}
